import {
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { SegmentTabItem } from './SegmentTabs.js';
import {
  analyticsSubTab,
  analyticsSubTabList,
  analyticsSubTabOverflowTrigger,
} from './segmentTabs.styles.js';

const LG_MIN_WIDTH_MQ = '(min-width: 1024px)';
const MAX_VISIBLE_BELOW_LG = 4;

export interface SegmentTabsOverflowProps {
  readonly tabs: readonly SegmentTabItem[];
  readonly activeId: string;
  readonly onChange: (id: string) => void;
  readonly ariaLabel?: string;
  /** Prefix for tab/panel ids (`{prefix}-tab-{id}` / `{prefix}-panel-{id}`). */
  readonly idPrefix?: string;
}

function useIsLgViewport(): boolean {
  const [isLg, setIsLg] = useState((): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia(LG_MIN_WIDTH_MQ).matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia(LG_MIN_WIDTH_MQ);
    const onChange = (): void => {
      setIsLg(mq.matches);
    };
    onChange();
    mq.addEventListener('change', onChange);
    return () => {
      mq.removeEventListener('change', onChange);
    };
  }, []);

  return isLg;
}

function splitVisibleAndOverflow(
  tabs: readonly SegmentTabItem[],
  activeId: string,
  showAll: boolean
): { visible: readonly SegmentTabItem[]; overflow: readonly SegmentTabItem[] } {
  if (showAll || tabs.length <= MAX_VISIBLE_BELOW_LG) {
    return { visible: tabs, overflow: [] };
  }

  const activeIdx = tabs.findIndex((tab) => tab.id === activeId);
  if (activeIdx < 0 || activeIdx < MAX_VISIBLE_BELOW_LG) {
    return {
      visible: tabs.slice(0, MAX_VISIBLE_BELOW_LG),
      overflow: tabs.slice(MAX_VISIBLE_BELOW_LG),
    };
  }

  const head = tabs.slice(0, MAX_VISIBLE_BELOW_LG - 1);
  const activeTab = tabs[activeIdx];
  if (activeTab == null) {
    return {
      visible: tabs.slice(0, MAX_VISIBLE_BELOW_LG),
      overflow: tabs.slice(MAX_VISIBLE_BELOW_LG),
    };
  }

  const visibleIds = new Set([...head.map((t) => t.id), activeTab.id]);
  return {
    visible: [...head, activeTab],
    overflow: tabs.filter((tab) => !visibleIds.has(tab.id)),
  };
}

/**
 * Linear-style sub-tab overflow (Plan §3.7).
 * Below 1024px: max 4 visible tabs + "+N další" popover for the rest.
 */
export const SegmentTabsOverflow = memo<SegmentTabsOverflowProps>(
  ({ tabs, activeId, onChange, ariaLabel, idPrefix }): JSX.Element => {
    const { t } = useTranslation('admin');
    const isLg = useIsLgViewport();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuId = useId();
    const rootRef = useRef<HTMLDivElement>(null);

    const { visible, overflow } = useMemo(
      () => splitVisibleAndOverflow(tabs, activeId, isLg),
      [tabs, activeId, isLg]
    );

    const closeMenu = useCallback((): void => {
      setMenuOpen(false);
    }, []);

    useEffect(() => {
      if (!menuOpen) {
        return;
      }
      const onPointerDown = (event: MouseEvent): void => {
        const root = rootRef.current;
        if (root != null && !root.contains(event.target as Node)) {
          closeMenu();
        }
      };
      const onKeyDown = (event: KeyboardEvent): void => {
        if (event.key === 'Escape') {
          closeMenu();
        }
      };
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('keydown', onKeyDown);
      return () => {
        document.removeEventListener('mousedown', onPointerDown);
        document.removeEventListener('keydown', onKeyDown);
      };
    }, [menuOpen, closeMenu]);

    const overflowCount = overflow.length;

    return (
      <div ref={rootRef} className="relative">
        <div className={analyticsSubTabList()} role="tablist" aria-label={ariaLabel}>
          {visible.map((tab) => {
            const isActive = tab.id === activeId;
            const tabId = idPrefix != null ? `${idPrefix}-tab-${tab.id}` : undefined;
            const panelId = idPrefix != null ? `${idPrefix}-panel-${tab.id}` : undefined;
            return (
              <button
                key={tab.id}
                id={tabId}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={panelId}
                tabIndex={isActive ? 0 : -1}
                className={analyticsSubTab({ active: isActive })}
                onClick={() => {
                  onChange(tab.id);
                  closeMenu();
                }}
              >
                {tab.label}
              </button>
            );
          })}
          {overflowCount > 0 ? (
            <button
              type="button"
              className={analyticsSubTabOverflowTrigger()}
              aria-expanded={menuOpen}
              aria-controls={menuId}
              aria-haspopup="menu"
              aria-label={t('analytics.tabs.overflowAria', { count: overflowCount })}
              data-testid="analytics-segment-tabs-overflow"
              onClick={() => {
                setMenuOpen((open) => !open);
              }}
            >
              {t('analytics.tabs.overflow', { count: overflowCount })}
            </button>
          ) : null}
        </div>
        {menuOpen && overflowCount > 0 ? (
          <div
            id={menuId}
            role="menu"
            className="absolute z-[var(--z-popover)] mt-1 min-w-[10rem] rounded-lg border border-[var(--color-an-border)] bg-[var(--color-an-surface)] p-1 shadow-[var(--elevation-2)]"
            data-testid="analytics-segment-tabs-overflow-menu"
          >
            {overflow.map((tab) => {
              const isActive = tab.id === activeId;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="menuitem"
                  className={[
                    'block w-full rounded-md px-3 py-1.5 text-left text-sm',
                    isActive
                      ? 'bg-[var(--color-an-primary-soft)] text-[var(--color-an-primary)]'
                      : 'text-[var(--color-an-text)] hover:bg-[var(--color-an-primary-soft)]',
                  ].join(' ')}
                  onClick={() => {
                    onChange(tab.id);
                    closeMenu();
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  }
);

SegmentTabsOverflow.displayName = 'SegmentTabsOverflow';
